import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, RefreshControl, StyleSheet, ViewToken } from 'react-native';

import { useIsFocused, useNavigation, useRoute, useScrollToTop } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { GetFilteredEntriesByPageQuery } from '@/api/entries/query.generated';
import { FilterChipItem, FilterChipList } from '@/components/FilterChipList';
import { Loading } from '@/components/Loading';
import { SizedBox } from '@/components/SizedBox';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { EntriesListItem } from '@/feature/entries-pickem/components/EntriesListItem';
import { FINISHED_TAB_FILTERS, FinishedTab } from '@/feature/entries-pickem/entries-utils';
import { getEntryCancelExpiry } from '@/feature/entries-pickem/entries-utils';
import { useCancelExpiryRefetch } from '@/feature/entries-pickem/hooks/use-cancel-expiry-refetch';
import { useInfiniteFilteredEntries } from '@/feature/entries-pickem/hooks/use-infinite-filtered-entries';
import { lobbyId } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { useResponsiveColumnCount } from '@/feature/responsive-design/hooks/use-responsive-column-count';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { useResumeEffect } from '@/hooks/use-resume';
import { useUserStore } from '@/hooks/use-user-store';
import { PickemEntriesTabNavigationProp } from '@/navigation/pickem/types';
import { designSystem } from '@/styles/styles';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { FlashList } from '@shopify/flash-list';

import EntriesEmptyList from '../components/EntriesEmptyList';
import { EntriesTabParamsList } from '../screens/EntriesTopTabBarScreen';

// FlashList is used on mobile for better performance and FlatList is kept on web because it supports columnWrapperStyle
// which lets us style the columns (gap, equal height cards)
const List = isWeb ? FlatList : FlashList;

const keyExtractor = ({ id }: EntryItemType) => id;

export type EntriesListParamList = NativeStackScreenProps<EntriesTabParamsList, 'Entries-UPCOMING'>;

export type EntryItemType = NonNullable<GetFilteredEntriesByPageQuery['getFilteredEntriesByPage']['content']>[number];
export type EntryItemPick = EntryItemType['picks'][number];

export const EntriesList = () => {
    const navigation = useNavigation<PickemEntriesTabNavigationProp>();
    const columnCount = useResponsiveColumnCount([1, 1, 2, 3, 4]);
    const multipleColumns = columnCount > 1;

    const {
        params: { currentSection },
    } = useRoute<EntriesListParamList['route']>();

    const [selectedFinishedFilter, setSelectedFinishedFilter] = useState<FinishedTab>('All');

    const activeFilter = useMemo(
        () =>
            currentSection.label === 'Finished'
                ? FINISHED_TAB_FILTERS.find(f => f.id === selectedFinishedFilter)
                : undefined,
        [currentSection.label, selectedFinishedFilter]
    );

    const { data, isLoading, isFetching, fetchNextPage, refetch } = useInfiniteFilteredEntries({
        eventStatus: currentSection.eventStatus,
        result: activeFilter?.result,
        status: activeFilter?.status,
    });

    const upcomingOrLiveTabSelected = currentSection.label !== 'Finished';

    const entries = useMemo(
        () =>
            (data?.pages ?? [])
                .map(page => page.content)
                .filter(Boolean)
                .flat(),
        [data?.pages]
    );

    const navigatePickemHome = useCallback(() => {
        navigation.navigate('Lobby', { screen: 'LobbyScreen', params: { league: lobbyId } });
    }, [navigation]);

    const { forceRefreshing, onRefresh } = useForceRefresh(refetch);

    const silentRefresh = useCallback(() => {
        if (useUserStore.getState().guest) {
            return;
        }
        refetch();
    }, [refetch]);

    useResumeEffect(silentRefresh);

    const [visibleEntryIds, setVisibleEntryIds] = useState<ReadonlySet<string>>(new Set());

    // Dependencies must stay empty because adding any dep changes the function reference between renders,
    // which triggers "Changing onViewableItemsChanged on the fly is not supported" (Invariant Violation) on WEB
    const onViewableItemsChanged = useCallback(({ viewableItems }: { viewableItems: ViewToken[] }) => {
        setVisibleEntryIds(new Set(viewableItems.map(({ item }) => (item as EntryItemType).id)));
    }, []);

    const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 50 });

    const listRef = useRef<FlashList<EntryItemType> | null>(null);

    useScrollToTop(listRef);

    const lastFetchedPageIndex = data?.pages?.length || 0;
    const totalPages = data?.pages?.[data?.pages.length - 1]?.totalPages || 0;
    const key = isWeb ? `${columnCount}-${isLoading}` : columnCount;
    const emptyListLabel =
        activeFilter && activeFilter.id !== 'All'
            ? activeFilter.labelKey.toLowerCase()
            : currentSection.label.toLowerCase();

    const refreshControl = useMemo(() => {
        return (
            <RefreshControl
                colors={[designSystem.colors.neutral700]}
                tintColor={ds.colorValue.icon.primary}
                refreshing={forceRefreshing}
                onRefresh={onRefresh}
            />
        );
    }, [forceRefreshing, onRefresh]);

    const renderItem = useCallback(
        ({ item }: { item: EntryItemType }) => {
            // On web, onViewableItemsChanged only fires after the first scroll, so visibleEntryIds
            // stays empty on mount and after data reloads. Fall back to treating all items as visible
            // until viewability tracking kicks in, so cashout polling starts correctly.
            const isWebViewabilityUntracked = isWeb && visibleEntryIds.size === 0;
            const isVisible = isWebViewabilityUntracked || visibleEntryIds.has(item.id);
            if (multipleColumns) {
                return (
                    <Box style={styles.columnItem} width={'100%'} paddingBottom="s16">
                        <EntriesListItem item={item} isVisible={isVisible} />
                    </Box>
                );
            }
            return (
                <Box paddingHorizontal="s16" paddingBottom="s16">
                    <EntriesListItem item={item} isVisible={isVisible} />
                </Box>
            );
        },
        [multipleColumns, visibleEntryIds]
    );

    const finishedChipItems = useMemo<FilterChipItem<FinishedTab>[]>(
        () =>
            FINISHED_TAB_FILTERS.map(filter => ({
                id: filter.id,
                label: filter.labelKey,
                testID: `entriesFilter-${filter.id}`,
            })),
        []
    );

    const listHeaderComponent = useMemo(() => {
        //when filters missing, add extra padding so cards are aligned horizontally across all tabs
        if (upcomingOrLiveTabSelected) {
            return <SizedBox value={24} />;
        }
        return (
            <Box paddingTop="s16" paddingBottom="s24">
                <FilterChipList
                    items={finishedChipItems}
                    selected={selectedFinishedFilter}
                    onSelect={setSelectedFinishedFilter}
                    contentContainerStyle={styles.chipListContent}
                />
            </Box>
        );
    }, [upcomingOrLiveTabSelected, finishedChipItems, selectedFinishedFilter]);

    const nextCancellationExpiry = useMemo(() => {
        const expiries = entries.flatMap(e => {
            const expiry = getEntryCancelExpiry(e);
            return expiry ? [expiry] : [];
        });
        return expiries.length > 0 ? Math.min(...expiries) : null;
    }, [entries]);

    const listEmptyComponent = useMemo(
        () =>
            isLoading ? null : (
                <Box paddingHorizontal="s16">
                    <EntriesEmptyList label={emptyListLabel} navigateHome={navigatePickemHome} />
                </Box>
            ),
        [isLoading, emptyListLabel, navigatePickemHome]
    );

    const isFocused = useIsFocused();
    useCancelExpiryRefetch(isFocused ? nextCancellationExpiry : null, refetch);

    return (
        <Box flex={1}>
            {isLoading && entries.length === 0 ? (
                <Box style={StyleSheet.absoluteFillObject} justifyContent={'center'} alignItems={'center'}>
                    <ActivityIndicator />
                </Box>
            ) : null}
            <List
                ref={listRef}
                // we have 4 different card heights: simple, with promos, with cash out, with promos and cash out
                // so we use "with promos" card height as the average (min. 199px - max. 323px)
                estimatedItemSize={255}
                key={key}
                columnWrapperStyle={isWeb && columnCount > 1 ? styles.columns : undefined}
                ListEmptyComponent={listEmptyComponent}
                numColumns={columnCount}
                data={entries}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                onViewableItemsChanged={onViewableItemsChanged}
                viewabilityConfig={viewabilityConfig.current}
                onEndReachedThreshold={1.5}
                onEndReached={() => !isFetching && fetchNextPage()}
                showsVerticalScrollIndicator={showScrollIndicator}
                ListHeaderComponent={listHeaderComponent}
                ListFooterComponent={isFetching && lastFetchedPageIndex < totalPages ? ListFooterComponent : null}
                refreshControl={refreshControl}
                extraData={visibleEntryIds}
            />
        </Box>
    );
};

const ListFooterComponent = () => (
    <Box paddingBottom={'s12'} paddingTop={'s24'}>
        <Loading />
    </Box>
);

const COLUMN_GAP = 10;

const styles = StyleSheet.create({
    columnItem: {
        paddingHorizontal: COLUMN_GAP / 2,
        flex: 1,
    },
    columns: {
        alignItems: 'stretch',
    },
    chipListContent: {
        paddingLeft: 16,
    },
});
